// Plan fit (D109, APS-361) on an organization's page: its usage and trend, what staff learned in
// sales conversations, its Pro bill now and in a year, what it costs to serve, the margin, and the
// verdict. Every figure shows how it was computed and the cost model version it used, so staff can
// audit it. It never changes a plan; the Quote link opens the calculator with its numbers.
import { ScrollX, Section, SectionHead, Strip } from '@apsio/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type FormEvent, Fragment, useId, useState } from 'react'
import { type PlanFit, planFitQuery, type StaffFlag, setStaffFlag } from '../api/queries'
import { Callout } from '../components/Callout'
import { ErrorState, Loading } from '../components/states'
import { toned } from '../components/toned'
import { type MessageId, useI18n } from '../i18n'
import { useFormat } from '../lib/format'

export const FLAG_LABEL: Record<StaffFlag, MessageId> = {
  sso: 'planFit.flag.sso',
  scim: 'planFit.flag.scim',
  retention: 'planFit.flag.retention',
  dpa: 'planFit.flag.dpa',
  sla: 'planFit.flag.sla',
  invoice: 'planFit.flag.invoice',
  security_review: 'planFit.flag.security_review',
  eu: 'planFit.flag.eu',
}

type Trigger = PlanFit['fit']['triggers'][number]['trigger']
const TRIGGER_LABEL: Record<Trigger, MessageId> = {
  sso_scim: 'planFit.trigger.sso_scim',
  retention: 'planFit.trigger.retention',
  contract: 'planFit.trigger.contract',
  volume: 'planFit.trigger.volume',
  spiky: 'planFit.trigger.spiky',
  low_margin: 'planFit.trigger.low_margin',
}

/** The verdict in the strip's figure: one short word (the full verdict is the sub-line's). */
const VERDICT_SHORT: Record<PlanFit['fit']['verdict'], MessageId> = {
  stay: 'planFit.verdictShort.stay',
  offer_enterprise: 'planFit.verdictShort.offer_enterprise',
  review_price: 'planFit.verdictShort.review_price',
}

export const VERDICT_LABEL: Record<PlanFit['fit']['verdict'], MessageId> = {
  stay: 'planFit.verdict.stay',
  offer_enterprise: 'planFit.verdict.offer_enterprise',
  review_price: 'planFit.verdict.review_price',
}

/** The verdict's status word under it: ✓ fits, ! Enterprise asks, ! under the floor. */
export function verdictWord(
  t: ReturnType<typeof useI18n>['t'],
  fit: PlanFit['fit'],
): { d: string; tone?: 'warn' | 'crash' } {
  if (fit.verdict === 'stay') return { d: `✓ ${t('planFit.word.fits')}` }
  if (fit.verdict === 'review_price')
    return { d: `! ${t('planFit.word.underFloor')}`, tone: 'crash' }
  return { d: `! ${t('planFit.word.triggers', { n: fit.score })}`, tone: 'warn' }
}

/** The staff flags, checked or not, with a note and the required reason (audited). */
function Flags({ orgId, data }: { orgId: string; data: PlanFit }) {
  const { t, date } = useI18n()
  const client = useQueryClient()
  const [on, setOn] = useState(() => new Map(data.flags.map((f) => [f.flag, f.on])))
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const ids = { note: useId(), reason: useId() }
  const changed = data.flags.filter((f) => on.get(f.flag) !== f.on)
  const reset = () => {
    setOn(new Map(data.flags.map((f) => [f.flag, f.on])))
    setNote('')
    setReason('')
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setFailed(false)
    try {
      for (const f of changed)
        await setStaffFlag({ orgId, flag: f.flag, on: on.get(f.flag) ?? false, note, reason })
      await Promise.all([
        client.invalidateQueries({ queryKey: ['plan-fit'] }),
        client.invalidateQueries({ queryKey: ['audit'] }),
      ])
      setNote('')
      setReason('')
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }
  return (
    <form className="win form pf-win" onSubmit={submit} aria-label={t('planFit.flags')}>
      <div className="win-bar">
        <b>{t('planFit.flags')}</b>
        <span>{t('planFit.flagsMeta')}</span>
      </div>
      <div className="form-body">
        <p className="dim pf-src">{t('planFit.flagsSource')}</p>
        <fieldset className="pf-flags">
          <legend className="vh">{t('planFit.flags')}</legend>
          {data.flags.map((f) => (
            <label key={f.flag} className="pf-flag">
              <input
                type="checkbox"
                checked={on.get(f.flag) ?? false}
                onChange={(e) => setOn(new Map(on).set(f.flag, e.target.checked))}
              />
              <span>
                {t(FLAG_LABEL[f.flag])}
                {f.set_at ? (
                  <span className="t2">
                    {f.note ? `${f.note} · ` : ''}
                    {date(f.set_at)}
                  </span>
                ) : null}
              </span>
            </label>
          ))}
        </fieldset>
        <div className="form-grid">
          <label className="lbl wide" htmlFor={ids.note}>
            {t('planFit.flagNote')}
            <input
              id={ids.note}
              className="input"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <label className="lbl wide" htmlFor={ids.reason}>
            {t('planFit.reason')}
            <input
              id={ids.reason}
              className="input"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        </div>
        <div className="acts">
          <button
            type="submit"
            className="btn solid"
            disabled={busy || changed.length === 0 || reason.trim() === ''}
          >
            {t('planFit.save')}
          </button>
          <button type="button" className="btn ghost" disabled={busy} onClick={reset}>
            {t('planFit.cancel')}
          </button>
        </div>
        {failed ? (
          <div role="alert">
            <Callout crash>{t('planFit.failed')}</Callout>
          </div>
        ) : null}
      </div>
    </form>
  )
}

/**
 * The staff flags on their own, for the organization page's side column (APS-412): the same
 * plan fit query as the panel, so one request feeds both.
 */
export function PlanFitFlags({ orgId }: { orgId: string }) {
  const q = useQuery(planFitQuery(orgId))
  if (!q.data) return null
  return <Flags key={JSON.stringify(q.data.flags)} orgId={orgId} data={q.data} />
}

/** One figure and how it was computed, as a key-value row with a "how" line under the value. */
function Row({ label, value, how }: { label: string; value: string; how: string }) {
  return (
    <Fragment>
      <dt>{label}</dt>
      <dd>{value}</dd>
      <dd className="how">{how}</dd>
    </Fragment>
  )
}

export function PlanFitPanel({ orgId }: { orgId: string }) {
  const { t } = useI18n()
  const { usd, pct, n } = useFormat()
  const q = useQuery(planFitQuery(orgId))
  if (q.isPending)
    return (
      <Section title={t('planFit.title')}>
        <Loading />
      </Section>
    )
  if (q.isError)
    return (
      <Section title={t('planFit.title')}>
        <ErrorState error={q.error} retry={() => q.refetch()} />
      </Section>
    )
  const v = q.data
  const f = v.fit
  const c = f.cost
  const word = verdictWord(t, f)
  const share = v.basis.captured_all_orgs > 0 ? f.captured / v.basis.captured_all_orgs : 0
  const marginTone =
    f.margin_share === null
      ? undefined
      : f.margin_share < 0
        ? ('crash' as const)
        : f.margin_share < v.basis.floor_margin
          ? ('warn' as const)
          : undefined
  return (
    <Section
      title={t('planFit.title')}
      meta={t('planFit.meta', { version: v.version })}
      right={
        <Link className="btn sm" to="/quote" search={{ org: orgId }}>
          {t('planFit.quote')}
        </Link>
      }
    >
      <div className="pf-body">
        <Strip
          items={toned([
            {
              dt: t('planFit.bill'),
              dd: usd(f.bill),
              d: t('planFit.bill12', { bill: usd(f.bill12) }),
            },
            {
              dt: t('planFit.cost'),
              dd: usd(c.total),
              d:
                f.per1k.cost === null
                  ? t('planFit.costMeta', { version: v.version })
                  : t('planFit.costPer1k', { cost: usd(f.per1k.cost, 3) }),
            },
            {
              dt: t('planFit.margin'),
              dd: f.margin_share === null ? '–' : pct(f.margin_share),
              d: t('planFit.marginMonthly', { margin: usd(f.margin) }),
              tone: marginTone,
            },
            {
              dt: t('planFit.verdict'),
              dd: t(VERDICT_SHORT[f.verdict]),
              d: `${word.d} · ${t(VERDICT_LABEL[f.verdict])}`,
              tone: word.tone,
            },
          ])}
        />

        <div className="grid2">
          <Section title={t('planFit.how')} meta={t('planFit.howMeta', { version: v.version })}>
            <dl className="kv pf-how">
              <Row
                label={t('planFit.row.captured')}
                value={n(f.captured)}
                how={
                  v.partial
                    ? t('planFit.how.capturedPartial', { share: pct(v.partial.elapsed_share) })
                    : t('planFit.how.captured')
                }
              />
              <Row
                label={t('planFit.row.trend')}
                value={t('planFit.perMonth', { n: n(f.slope) })}
                how={t('planFit.how.trend', { months: v.months.length, in12: n(f.captured12) })}
              />
              <Row
                label={t('planFit.row.bill')}
                value={usd(f.bill)}
                how={t('planFit.how.bill', { captured: n(f.captured) })}
              />
              <Row
                label={t('planFit.row.bill12')}
                value={usd(f.bill12)}
                how={t('planFit.how.bill', { captured: n(f.captured12) })}
              />
              <Row
                label={t('planFit.row.variable')}
                value={usd(c.variable)}
                how={t('planFit.how.variable', {
                  captured: n(f.captured),
                  records: n(v.profile.records_per_session),
                  replay: pct(v.profile.replay_share),
                  minutes: n(v.profile.replay_minutes),
                })}
              />
              <Row
                label={t('planFit.row.summaries')}
                value={usd(c.summaries)}
                how={t('planFit.how.summaries', { counted: n(f.counted) })}
              />
              <Row
                label={t('planFit.row.crashes')}
                value={usd(c.crashes)}
                how={t('planFit.how.crashes')}
              />
              <Row
                label={t('planFit.row.fixed')}
                value={usd(c.fixed_share)}
                how={t('planFit.how.fixed', {
                  fixed: usd(v.basis.fixed_monthly, 0),
                  share: pct(Math.max(0.01, share)),
                  all: n(v.basis.captured_all_orgs),
                })}
              />
              {c.sessionless + c.symbols > 0 ? (
                <Row
                  label={t('planFit.row.unbilledInCost')}
                  value={usd(c.sessionless + c.symbols)}
                  how={t('planFit.how.unbilledInCost', {
                    sessionless: usd(c.sessionless),
                    symbols: usd(c.symbols),
                  })}
                />
              ) : null}
              <Row
                label={t('planFit.row.payment')}
                value={usd(c.payment)}
                how={t('planFit.how.payment', { currency: v.facts.currency.toUpperCase() })}
              />
              <Row
                label={t('planFit.row.tax')}
                value={usd(c.tax)}
                how={t('planFit.how.tax', { rate: pct(v.basis.tax_rate) })}
              />
              <Row
                label={t('planFit.row.margin')}
                value={usd(f.margin)}
                how={t('planFit.how.margin', {
                  bill: usd(f.bill),
                  cost: usd(c.total),
                  floor: pct(v.basis.floor_margin),
                })}
              />
            </dl>
          </Section>

          <Section title={t('planFit.triggers')} meta={t('planFit.score', { n: f.score })}>
            {f.triggers.length === 0 ? (
              <p className="dim">{t('planFit.noTriggers')}</p>
            ) : (
              <ul className="pf-triggers">
                {f.triggers.map((x) => (
                  <li key={x.trigger}>
                    <span className={x.trigger === 'low_margin' ? 'st fire' : 'st miss'}>
                      <span aria-hidden="true">! </span>
                      <span>{t(TRIGGER_LABEL[x.trigger])}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <SectionHead minor title={t('planFit.unbilled')} meta={t('planFit.unbilledMeta')} />
            <p className="t2 pf-note">{t('planFit.unbilledHow')}</p>
            {f.unbilled ? (
              <dl className="kv">
                {(
                  [
                    ['planFit.unbilled.summaries', f.unbilled.summaries],
                    ['planFit.unbilled.errors', f.unbilled.errors],
                    ['planFit.unbilled.telemetry', f.unbilled.telemetry],
                    ['planFit.unbilled.sessionless', f.unbilled.sessionless],
                    ['planFit.unbilled.symbols', f.unbilled.symbols],
                    ['planFit.unbilled.total', f.unbilled.total],
                  ] as [MessageId, number][]
                ).map(([label, value]) => (
                  <Fragment key={label}>
                    <dt>{t(label)}</dt>
                    <dd>{usd(value, 3)}</dd>
                  </Fragment>
                ))}
              </dl>
            ) : (
              <p className="dim">{t('planFit.unbilledNone')}</p>
            )}
          </Section>
        </div>

        <div>
          <SectionHead
            minor
            title={t('planFit.usage')}
            meta={t('planFit.facts', {
              members: v.facts.members,
              projects: v.facts.projects,
              apps: v.facts.apps,
              queries: v.facts.queries30d,
            })}
          />
          <ScrollX label={t('planFit.usage')}>
            <table className="t">
              <thead>
                <tr>
                  <th className="f" scope="col">
                    {t('planFit.col.month')}
                  </th>
                  <th className="num" scope="col">
                    {t('planFit.col.captured')}
                  </th>
                  <th className="num" scope="col">
                    {t('planFit.col.counted')}
                  </th>
                  <th className="num" scope="col">
                    {t('planFit.col.replay')}
                  </th>
                  <th className="last" scope="col">
                    {t('planFit.col.spendCap')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {v.months.map((m) => (
                  <tr key={m.month}>
                    <td className="f mono">{m.month}</td>
                    <td className="num">{n(m.captured)}</td>
                    <td className="num">{n(m.counted)}</td>
                    <td className="num">{n(m.replay_sessions)}</td>
                    <td className="last">
                      {m.spend_capped ? (
                        <span className="st miss">
                          <span aria-hidden="true">! </span>
                          <span>{t('planFit.capped')}</span>
                        </span>
                      ) : (
                        '–'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </div>
      </div>
    </Section>
  )
}
